import { OperatorData, FleetAbuse, FleetInsight } from './types';

// Helper function to generate random abuse names
const abuseNames = [
  'Incorrect Driving Initiation - Starting vehicle improperly',
  'Truck Loading Abuse - Improper Loading Technique', 
  'Hot Engine Shut Off Without Cooldown Period',
  'Hydraulic Brake Usage While Moving at High Speed',
  'Wheel Digging and Excessive Tire Spin',
  'Wrong Gear During Loading Operations',
  'Excessive Speed in Restricted Areas',
  'Improper Turning Technique',
  'Brake Overheating from Excessive Use',
  'Transmission Abuse During Gear Changes'
];

// Helper function to get a random abuse
const getRandomAbuse = (index: number) => {
  const name = abuseNames[index % abuseNames.length];
  return {
    name,
    fleetRank: Math.floor(Math.random() * 30) + 1
  };
};

// Generate names for drivers
const firstNames = ['Jack', 'Oven', 'Tyson', 'Brisde', 'Shane', 'Kyle', 'Mitchell', 'Nathan', 'Daniel', 'Travis', 
  'Brett', 'Corey', 'James', 'Robert', 'John', 'Michael', 'David', 'William', 'Richard', 'Thomas',
  'Charles', 'Christopher', 'Matthew', 'Mark', 'Donald', 'Kenneth', 'Steven', 'Edward', 'Brian', 'Ronald',
  'Anthony', 'Kevin', 'Jason', 'Jeffrey', 'Ryan', 'Jacob', 'Gary', 'Nicholas', 'Eric', 'Stephen',
  'Jonathan', 'Larry', 'Justin', 'Scott', 'Brandon', 'Frank', 'Benjamin', 'Gregory', 'Samuel', 'Raymond'];

// Generate asset IDs
const generateAssetId = (index: number) => {
  const prefix = ['628', '907', '134', '677', '44a', '359', '244', '160', '116', '629', '94c', '578'];
  const suffix = ['f19746', '241683', 'd62970', 'b03284', '7f89013', '920774', '86713s', 't214659', '606432', '74d581', '072388', '23f004'];
  return `${prefix[index % prefix.length]}${suffix[index % suffix.length]}${index}`;
};

// Generate 500 drivers - memoized to prevent regeneration
const generateMockData = (): OperatorData[] => {
  // Use fixed seed for consistent random values
  const seededRandom = (seed: number) => {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  };
  
  return Array.from({ length: 500 }, (_, index) => {
    const rank = index + 1;
    const eventsDuration = Math.max(10, 300 - rank * 0.5 + Math.floor(seededRandom(rank) * 50));
    const trendValue = Math.floor(seededRandom(rank * 2) * 121) - 60; // -60 to +60
    const operatorName = firstNames[index % firstNames.length] + (index >= firstNames.length ? index.toString() : '');
    const topEvents = {
      first: { name: abuseNames[index % abuseNames.length], fleetRank: (index % 30) + 1 },
      second: { name: abuseNames[(index + 1) % abuseNames.length], fleetRank: ((index + 1) % 30) + 1 },
      third: { name: abuseNames[(index + 2) % abuseNames.length], fleetRank: ((index + 2) % 30) + 1 }
    };

    return {
      id: rank.toString(),
      rank,
      name: operatorName,
      count: Math.floor(eventsDuration),
      trend: trendValue,
      topEvents,
      // Legacy fields for compatibility
      operatorName,
      assetName: generateAssetId(index),
      siteName: 'Ulan West',
      eventsDuration: Math.floor(eventsDuration),
      eventsTrend: trendValue,
      topAbuses: topEvents,
      topDominantAbusesAndAbusesRank: abuseNames[index % abuseNames.length].split(' - ')[0]
    };
  });
};

// Generate once and export
export const mockOperatorData: OperatorData[] = generateMockData();

export const mockFleetAbuses: FleetAbuse[] = [
  { id: '1', abuseName: 'Incorrect Driving Initiation', count: 245, percentage: 41 },
  { id: '2', abuseName: 'Wrong Gear During Loading', count: 90, percentage: 15 },
  { id: '3', abuseName: 'Hydraulic Brake Usage', count: 66, percentage: 11 },
  { id: '4', abuseName: 'Wheel Digging', count: 48, percentage: 8 },
  { id: '5', abuseName: 'Hot Engine Shut Off', count: 24, percentage: 4 },
  { id: '6', abuseName: 'Truck loading Abuse', count: 24, percentage: 4 }
];

export const mockFleetInsights: FleetInsight[] = [
  {
    id: '1',
    title: 'Increased Risky Driving',
    description: 'Sharp turns and sudden braking incidents up by 15% over the last week',
    value: '+15%',
    trend: 'up',
    severity: 3
  },
  {
    id: '2',
    title: 'Load & Compliance Management',
    description: 'Reduced overload occurrences by 20% over the past month, saving wear and tear on equipment',
    value: '-20%',
    trend: 'down',
    severity: 1
  },
  {
    id: '3',
    title: 'Maintenance Alerts',
    description: '3 trucks require immediate attention based on abnormal wear patterns detected',
    value: '3',
    trend: 'up',
    severity: 3
  },
  {
    id: '4',
    title: 'Fuel Efficiency Drop',
    description: 'Average fleet fuel efficiency has decreased by 8% this quarter',
    value: '-8%',
    trend: 'down',
    severity: 2
  }
];